import { NOTIFICATIONS_QUERY_KEYS } from '@/features/notifications/hooks/use-notifications';
import { notificationsQueryOptions, unreadCountQueryOptions } from '@/api/queries/notifications';
import { accountSettingsQueryOptions } from '@/api/queries/settings';
import { activityFeedQueryOptions, userFeedQueryOptions } from '@/api/queries/activity';
import { businessesTrendingQueryOptions, cuisinesQueryOptions } from '@/api/queries/businesses';
import { PROFILE_QUERY_KEYS, currentUserQueryOptions, userTasteQueryOptions } from '@/api/queries/users';
import { QueryClient, useQueryClient } from '@tanstack/react-query';

/**
 * Prefetch essential data for app initialization
 */
export class AppDataPrefetcher {
  private queryClient: QueryClient;

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  /**
   * Prefetch current user profile data
   * This is called during app initialization for better UX
   * Uses domain-oriented query options
   * @param userId - The current user's ID (required for API endpoint)
   */
  async prefetchCurrentUserProfile(userId: string): Promise<void> {
    try {
      if (!userId) {
        console.warn('Cannot prefetch profile: no user ID available');
        return;
      }

      await this.queryClient.prefetchQuery(currentUserQueryOptions(userId));

    } catch (error) {
      console.warn('Failed to prefetch current user profile:', error);
      // Don't throw - prefetching failures should not break app initialization
    }
  }

  /**
   * Prefetch the first page of the activity feed (default landing screen)
   */
  async prefetchActivityFeed(): Promise<void> {
    try {
      await this.queryClient.prefetchInfiniteQuery(activityFeedQueryOptions());
    } catch (error) {
      console.warn('Failed to prefetch activity feed:', error);
    }
  }

  /**
   * Prefetch the location-independent Discover queries (trending + cuisines).
   * Nearby is intentionally excluded: it needs a location fix that the shared
   * location provider only resolves after auth, so coords are not yet available
   * here. Nearby is instead served from the persisted cache on a warm boot.
   *
   * The params MUST mirror the Discover screen (`use-discover-data.ts`) exactly,
   * or the warmed entry lands under a key the screen never reads.
   */
  async prefetchDiscover(): Promise<void> {
    await Promise.allSettled([
      this.queryClient.prefetchQuery(cuisinesQueryOptions()),
      this.queryClient.prefetchQuery(businessesTrendingQueryOptions({ limit: 10 })),
    ]);
  }

  /**
   * Prefetch the notifications list so the screen paints from cache, not just
   * the badge count.
   */
  async prefetchNotificationsList(): Promise<void> {
    try {
      await this.queryClient.prefetchQuery(notificationsQueryOptions());
    } catch (error) {
      console.warn('Failed to prefetch notifications list:', error);
    }
  }

  /**
   * Prefetch the two profile queries the default (activity) tab needs beyond the
   * current-user profile: the user's own feed and taste distribution.
   */
  async prefetchProfileDefaultView(userId: string): Promise<void> {
    if (!userId) return;
    await Promise.allSettled([
      this.queryClient.prefetchInfiniteQuery(userFeedQueryOptions(userId)),
      this.queryClient.prefetchQuery(userTasteQueryOptions(userId)),
    ]);
  }

  /**
   * Prefetch the account settings document so units, default landing screen and
   * notification preferences resolve from cache (and disk, since it is persisted)
   * on cold boot rather than flashing defaults.
   */
  async prefetchAccountSettings(): Promise<void> {
    try {
      await this.queryClient.prefetchQuery(accountSettingsQueryOptions());
    } catch (error) {
      console.warn('Failed to prefetch account settings:', error);
    }
  }

  /**
   * Prefetch unread notifications count
   * This is called during app initialization to show badge count immediately
   */
  async prefetchUnreadCount(): Promise<void> {
    try {
      await this.queryClient.prefetchQuery(unreadCountQueryOptions());
    } catch (error) {
      console.warn('Failed to prefetch unread count:', error);
      // Don't throw - prefetching failures should not break app initialization
    }
  }

  /**
   * Prefetch multiple essential data points for the app
   * @param userId - The current user's ID (required for profile prefetch)
   */
  async prefetchEssentialData(userId?: string): Promise<void> {

    const prefetchPromises: Promise<void>[] = [];

    if (userId) {
      prefetchPromises.push(this.prefetchCurrentUserProfile(userId));
      // Profile default (activity) tab: user feed + taste.
      prefetchPromises.push(this.prefetchProfileDefaultView(userId));
    }

    // Account settings: units, default landing screen, notification prefs.
    prefetchPromises.push(this.prefetchAccountSettings());

    // Notifications: badge count + the list behind it.
    prefetchPromises.push(this.prefetchUnreadCount());
    prefetchPromises.push(this.prefetchNotificationsList());

    // Activity feed (default landing screen).
    prefetchPromises.push(this.prefetchActivityFeed());

    // Discover's location-independent rails (trending + cuisines).
    prefetchPromises.push(this.prefetchDiscover());

    try {
      await Promise.allSettled(prefetchPromises);
    } catch (error) {
      console.warn('Some prefetch operations failed:', error);
      // Continue app initialization even if prefetch fails
    }
  }

  /**
   * Clear all cached data (useful for logout)
   */
  async clearAllCachedData(): Promise<void> {
    await this.queryClient.clear();
  }

  /**
   * Invalidate user-specific data (useful for user switching)
   */
  async invalidateUserData(): Promise<void> {

    await Promise.all([
      this.queryClient.invalidateQueries({
        queryKey: PROFILE_QUERY_KEYS.all,
      }),
      this.queryClient.invalidateQueries({
        queryKey: NOTIFICATIONS_QUERY_KEYS.all,
      }),
      // Add more user-specific query invalidations here
    ]);

  }
}

/**
 * Utility function to create and get app data prefetcher instance
 */
export function createAppDataPrefetcher(queryClient: QueryClient): AppDataPrefetcher {
  return new AppDataPrefetcher(queryClient);
}

/**
 * Hook to get prefetcher instance from the React context
 */
export function useAppDataPrefetcher(): AppDataPrefetcher {
  const queryClient = useQueryClient();
  return createAppDataPrefetcher(queryClient);
}
